// In-memory cache for an item-order's loaded data bundle.
//
// One entry per orderId. Stores the five backend fetches that getExistingDraperyOrder
// makes (getAllInfo + the four follow-up searches). TTL is short — we cache to
// absorb repeat opens within a session, not to replace network truth.
//
// Cache writes happen at the end of a successful load. Invalidation happens on
// successful save. Cache is in-memory only — page refresh clears it.

import { cloneCacheValue } from './cache-utils';

const DEFAULT_TTL_MS = 60000;

interface OrderCacheEntry {
  draperyOrderAllInfo: any;
  resWork: any;
  liningRes: any | null;
  summaryRes: any | null;
  embellishmentRes: any | null;
  fetchedAt: number;
}

export type OrderCachedBundle = Omit<OrderCacheEntry, 'fetchedAt'>;

const cache = new Map<number, OrderCacheEntry>();

export function getOrderCache(
  orderId: number,
  ttlMs: number = DEFAULT_TTL_MS
): OrderCachedBundle | null {
  if (!orderId) return null;
  const entry = cache.get(orderId);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > ttlMs) {
    cache.delete(orderId);
    return null;
  }
  const { fetchedAt: _f, ...rest } = entry;
  return cloneCacheValue(rest);
}

export function setOrderCache(orderId: number, data: OrderCachedBundle): void {
  if (!orderId) return;
  cache.set(orderId, { ...cloneCacheValue(data), fetchedAt: Date.now() });
}

export function invalidateOrderCache(orderId: number): void {
  if (!orderId) return;
  cache.delete(orderId);
}

export function clearAllOrderCache(): void {
  cache.clear();
}

export function getOrderCacheSize(): number {
  return cache.size;
}
